import { c as _c } from "react/compiler-runtime";
// Модалки оплаты по макету «5.1 Оформление заказа-2»: «Добавить карту»,
// «Счёт на оплату» (ИНН → организация, подтверждение юрлица), «Счёт №…»
// (превью документа, скачать/поделиться, перейти к заказу), «Указать
// электронную почту» и «Заказ оформлен».
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, type BillingProfile, type BuyerCompanySuggestion, type BuyerPaymentMethod, type Invoice } from "@/api";
import { SavedCardHint } from "@/components/buyer/payment-method-form";
import { Download20Icon, ShareAlt20Icon, XIcon } from "@/components/icons";
import { InvoiceView } from "@/components/orders/invoice-view";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { CheckoutModal, CheckRow, ModalButton, ModalField } from "./ui";
const digits = (v: string) => v.replace(/\D/g, "");

// ── Добавить карту ────────────────────────────────────────────────────────────
// fd-payment-01: номер карты, срок и CVC на нашем сайте не вводятся — только
// на странице платёжной системы. Карта сохраняется при оплате заказа с
// отметкой «Сохранить карту»: шлюз получает от провайдера токен и маску.
// Своей формы и выдуманного в браузере токена больше нет.
export function AddCardModal(t0) {
  const $ = _c(7);
  const {
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SavedCardHint />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onOpenChange) {
    t2 = <ModalButton className="mt-4" variant="dark" onClick={() => onOpenChange(false)} data-testid="card-save">Понятно</ModalButton>;
    $[1] = onOpenChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onOpenChange || $[4] !== open || $[5] !== t2) {
    t3 = <CheckoutModal open={open} onOpenChange={onOpenChange} title={"\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043A\u0430\u0440\u0442\u0443"} testId="add-card-modal">{t1}{t2}</CheckoutModal>;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// ── Счёт на оплату ───────────────────────────────────────────────────────────
// Счёт выписывается по реквизитам покупателя (billing profile) — поэтому они
// сохраняются в аккаунте всегда; флажок «Сохранить…» показан включённым.
export function InvoiceRequisitesModal({
  open,
  onOpenChange,
  billing,
  onSaved
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billing: BillingProfile | null;
  onSaved: (profile: BillingProfile) => void;
}) {
  const [inn, setInn] = useState("");
  const [company, setCompany] = useState<BuyerCompanySuggestion | null>(null);
  const [manualName, setManualName] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [looked, setLooked] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setInn(billing?.inn ?? "");
    setCompany(billing ? {
      inn: billing.inn,
      shortName: billing.companyName,
      fullName: billing.companyName,
      legalAddress: billing.legalAddress,
      kpp: billing.kpp,
      ogrn: billing.ogrn
    } : null);
    setManualName("");
    setManualAddress("");
    setLooked(Boolean(billing));
    setConfirm(false);
  }, [open, billing]);
  const innOk = /^(\d{10}|\d{12})$/.test(inn);
  useEffect(() => {
    if (!open || !innOk || company?.inn === inn) return;
    let alive = true;
    setLooked(false);
    void api.buyer.suggestCompany(inn).then(r => {
      if (!alive) return;
      setCompany(r.find(c => c.inn === inn) ?? r[0] ?? null);
      setLooked(true);
    }).catch(() => alive && setLooked(true));
    return () => {
      alive = false;
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: lookup on a complete INN
  }, [inn, innOk, open]);
  const name = company?.fullName || company?.shortName || manualName.trim();
  const ready = innOk && Boolean(name) && confirm;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const saved = await api.buyer.updateBillingProfile({
        companyName: company?.shortName || name,
        inn,
        kpp: company?.kpp ?? billing?.kpp,
        ogrn: company?.ogrn ?? billing?.ogrn,
        legalAddress: company?.legalAddress || manualAddress.trim() || billing?.legalAddress,
        bankName: billing?.bankName,
        bankAccount: billing?.bankAccount,
        corrAccount: billing?.corrAccount,
        bik: billing?.bik,
        contactName: billing?.contactName,
        contactEmail: billing?.contactEmail,
        contactPhone: billing?.contactPhone
      });
      onSaved(saved);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить реквизиты");
    } finally {
      setBusy(false);
    }
  };
  return <CheckoutModal open={open} onOpenChange={onOpenChange} title="Счёт на оплату" testId="invoice-modal">
			<ModalField label="ИНН организации или ИП" placeholder="10 или 12 цифр" inputMode="numeric" value={inn} valid={innOk} onChange={v => {
      setInn(digits(v).slice(0, 12));
      setCompany(null);
    }} trailing={null} testId="invoice-inn" />
			<div className="min-h-[160px] py-5">
				{innOk && company ? <div className="rounded-[12px] bg-[#F8F8F8] p-2.5" data-testid="invoice-company">
						<p className="text-[16px] leading-[22px] font-medium text-[#1F1F1F]">{company.fullName || company.shortName}</p>
						<p className="mt-2.5 text-[14px] leading-[18px] text-[#1F1F1F]">
							ИНН: {company.inn}
							{company.kpp ? ` КПП: ${company.kpp}` : ""}
						</p>
						{company.legalAddress && <p className="text-[14px] leading-[18px] text-[#1F1F1F]">{company.legalAddress}</p>}
					</div> : innOk && looked ? <div className="flex flex-col gap-[14px]">
						<p className="text-[12px] text-[#1F1F1F]/64">Организация не найдена в справочнике — заполните вручную</p>
						<ModalField label="Название организации" value={manualName} onChange={setManualName} testId="invoice-company-name" />
						<ModalField label="Юридический адрес" value={manualAddress} onChange={setManualAddress} />
					</div> : null}
			</div>
			<CheckRow checked={confirm} onChange={setConfirm} testId="invoice-confirm">
				Я подтверждаю, что действую от лица организации и принимаю условия оплаты от юридического лица
			</CheckRow>
			{/* Счёт выставляется по реквизитам из аккаунта — они сохраняются всегда. */}
			<CheckRow checked disabled className="mt-2.5 cursor-default items-center">
				Сохранить данные компании в аккаунте
			</CheckRow>
			<ModalButton className="mt-2.5" variant={ready ? "dark" : "light"} disabled={!ready || busy} onClick={() => void submit()} data-testid="invoice-submit">
				Сформировать счёт
			</ModalButton>
		</CheckoutModal>;
}

// ── Счёт №… (превью) ─────────────────────────────────────────────────────────
export function InvoicePreviewModal(t0) {
  const $ = _c(38);
  const {
    open,
    onOpenChange,
    invoice,
    loading,
    onGoToOrder,
    onDownload
  } = t0;
  const title = invoice ? `Счёт №${invoice.number}` : "\u0421\u0447\u0451\u0442";
  let t1;
  if ($[0] !== invoice) {
    t1 = invoice ? new Date(invoice.issuedAt).toLocaleDateString("ru-RU") : "";
    $[0] = invoice;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const date = t1;
  let t2;
  if ($[2] !== invoice?.orderId || $[3] !== title) {
    t2 = async () => {
      const url = `${window.location.origin}/profile/orders/${encodeURIComponent(invoice?.orderId ?? "")}`;
      try {
        if (navigator.share) {
          await navigator.share({
            title,
            url
          });
        } else {
          await navigator.clipboard.writeText(url);
          toast.success("\u0421\u0441\u044B\u043B\u043A\u0430 \u043D\u0430 \u0437\u0430\u043A\u0430\u0437 \u0441\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u0430");
        }
      } catch {}
    };
    $[2] = invoice?.orderId;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const share = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="grid h-[50px] w-[38px] shrink-0 place-items-center rounded-[4px] bg-[#E1574F] text-[10px] font-bold text-white">PDF</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = invoice?.number ?? "";
  let t5;
  if ($[6] !== date || $[7] !== t4) {
    t5 = <span className="block truncate text-[16px] leading-[22px] text-[#1F1F1F]">Счет на оплату {t4} от {date}.pdf</span>;
    $[6] = date;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="block text-[12px] text-[#1F1F1F]/48">Документ для оплаты по безналичному расчёту</span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div className="flex items-center gap-2.5">{t3}<span className="min-w-0">{t5}{t6}</span></div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== invoice || $[13] !== loading) {
    t8 = <div className="mt-5 h-[488px] overflow-auto rounded-[12px] border border-[#1F1F1F]/8 p-3 [&_article]:origin-top-left [&_article]:scale-[0.82] [&_article]:[width:122%]">{loading || !invoice ? <div className="grid h-full place-items-center text-[14px] text-[#1F1F1F]/48"><span className="text-[14px]">Загружаем счёт…</span></div> : <InvoiceView invoice={invoice} />}</div>;
    $[12] = invoice;
    $[13] = loading;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = !invoice;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Download20Icon size={20} />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== onDownload || $[17] !== t9) {
    t11 = <button type="button" onClick={onDownload} disabled={t9} className="flex h-12 items-center justify-center gap-2 text-[16px] text-[#1F1F1F] outline-none hover:text-[#FA5C2E] disabled:opacity-40">{t10} Скачать</button>;
    $[16] = onDownload;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== share) {
    t12 = () => void share();
    $[19] = share;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = !invoice;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ShareAlt20Icon size={20} />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t12 || $[23] !== t13) {
    t15 = <button type="button" onClick={t12} disabled={t13} className="flex h-12 items-center justify-center gap-2 text-[16px] text-[#1F1F1F] outline-none hover:text-[#FA5C2E] disabled:opacity-40">{t14} Поделиться</button>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t11 || $[26] !== t15) {
    t16 = <div className="mt-3 grid grid-cols-2">{t11}{t15}</div>;
    $[25] = t11;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== onGoToOrder) {
    t17 = <ModalButton className="mt-3" onClick={onGoToOrder} data-testid="invoice-go-order">Перейти к заказу</ModalButton>;
    $[28] = onGoToOrder;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== onOpenChange || $[31] !== open || $[32] !== t16 || $[33] !== t17 || $[34] !== t7 || $[35] !== t8 || $[36] !== title) {
    t18 = <CheckoutModal open={open} onOpenChange={onOpenChange} title={title} testId="invoice-preview-modal">{t7}{t8}{t16}{t17}</CheckoutModal>;
    $[30] = onOpenChange;
    $[31] = open;
    $[32] = t16;
    $[33] = t17;
    $[34] = t7;
    $[35] = t8;
    $[36] = title;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}

// ── Указать электронную почту ────────────────────────────────────────────────
export function EmailModal({
  open,
  onOpenChange,
  initial,
  purpose = "invoice",
  onSaved
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: string;
  purpose?: "invoice" | "receipt";
  onSaved: (email: string) => void | Promise<void>;
}) {
  const [email, setEmail] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setEmail(initial);
  }, [open, initial]);
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return <CheckoutModal open={open} onOpenChange={onOpenChange} title="Указать электронную почту" testId="email-modal">
			<ModalField label={purpose === "invoice" ? "Почта для отправки счёта" : "Почта для отправки чека"} placeholder="example@example.com" type="email" value={email} onChange={setEmail} testId="invoice-email" />
			<p className="mt-1.5 text-[12px] leading-4 font-medium text-[#171A24]/64">Заполните один раз и e-mail сохранится в личном кабинете</p>
			<ModalButton className="mt-5" variant="dark" disabled={!ok || busy} data-testid="email-save" onClick={async () => {
      setBusy(true);
      try {
        await onSaved(email.trim());
      } finally {
        setBusy(false);
      }
    }}>
				Сохранить
			</ModalButton>
		</CheckoutModal>;
}

// ── Заказ оформлен ───────────────────────────────────────────────────────────
// Модалка 383 на #F8F8F8 r20, отступы 24/16, промежутки 20 (кадр 3924:18727):
// заголовок 20/23 w600, превью товаров 77×102 r4 через 10 (подпись «N шт.»
// на белом 36 % с размытием), кнопки 170×40 r10 14/18 w600 «Перейти к заказу»
// / «Продолжить покупки».
export function OrderDoneModal(t0) {
  const $ = _c(29);
  const {
    open,
    onOpenChange,
    items,
    onGoToOrder,
    onContinue,
    nextPayment
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogTitle className="text-center text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#161616]">Заказ оформлен</DialogTitle>;
    t2 = <DialogDescription className="sr-only">Заказ оформлен</DialogDescription>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onOpenChange) {
    t3 = () => onOpenChange(false);
    $[2] = onOpenChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <XIcon size={24} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <div className="relative flex min-h-6 items-center justify-center px-6">{t1}{t2}<button type="button" onClick={t3} aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"} className="absolute top-1/2 right-0 grid size-6 -translate-y-1/2 place-items-center rounded-full text-[#1F1F1F] hover:text-[#1F1F1F]/70">{t4}</button></div>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== items) {
    t6 = items.slice(0, 4).map(_temp);
    $[7] = items;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <div className="flex justify-center gap-2.5 overflow-hidden">{t6}</div>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== nextPayment) {
    t8 = nextPayment && <div className="flex flex-col gap-2.5 rounded-[12px] bg-white p-3" data-testid="done-next-payment"><p className="text-[14px] leading-[18px] text-[#161616]">{nextPayment.label}</p><Button variant="accent" size="md" onClick={nextPayment.onPay} data-testid="done-pay-next">Оплатить второй заказ</Button></div>;
    $[11] = nextPayment;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== onGoToOrder) {
    t9 = <Button variant="gray" size="md" onClick={onGoToOrder} data-testid="done-go-order">Перейти к заказу</Button>;
    $[13] = onGoToOrder;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onContinue) {
    t10 = <Button variant="accent" size="md" onClick={onContinue}>Продолжить покупки</Button>;
    $[15] = onContinue;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <div className="grid grid-cols-2 gap-2.5">{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5 || $[22] !== t7 || $[23] !== t8) {
    t12 = <DialogContent showCloseButton={false} overlayClassName="bg-black/20 backdrop-blur-[2px]" data-testid="order-done-modal" className="flex w-[383px] max-w-[calc(100%-2rem)] flex-col gap-5 rounded-[20px] border-0 bg-[#F8F8F8] px-4 py-6 shadow-none sm:max-w-[383px]">{t5}{t7}{t8}{t11}</DialogContent>;
    $[20] = t11;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== onOpenChange || $[26] !== open || $[27] !== t12) {
    t13 = <Dialog open={open} onOpenChange={onOpenChange}>{t12}</Dialog>;
    $[25] = onOpenChange;
    $[26] = open;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
function _temp(i) {
  return <div key={i.id} className="relative h-[102px] w-[77px] shrink-0 overflow-hidden rounded-[4px] bg-white">{i.photo && <img src={i.photo} alt={i.name} className="size-full object-cover" />}{i.qty > 1 && <span className="absolute right-0 bottom-0 rounded-tl-[2px] rounded-br-[2px] bg-white/36 px-0.5 text-[12px] leading-4 text-[#1F1F1F] backdrop-blur-[2px]">{i.qty} шт.</span>}</div>;
}